import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  LayoutDashboard, 
  Layers, 
  ListTree, 
  Users, 
  Store, 
  Calculator, 
  LogOut,
  User,
  FileText
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/categories', label: 'Categories', icon: Layers },
  { path: '/subcategories', label: 'Sub-Categories', icon: ListTree },
  { path: '/vendors', label: 'Vendors', icon: Store },
  { path: '/budget-planner', label: 'Budget Planner', icon: Calculator },
  { path: '/users', label: 'User Management', icon: Users },
  { path: '/logs', label: 'Activity Logs', icon: FileText },
];

export const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#F7F0E8]">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-[#F7F0E8] border-r border-[#E6DCD3] p-6 flex flex-col z-50">
        {/* Logo */}
        <NavLink to="/dashboard" className="block mb-8">
          <h1 className="font-serif text-2xl font-semibold text-[#69111F] tracking-tight">
            KNOTS
          </h1>
          <p className="text-xs text-[#585858] uppercase tracking-widest mt-1">
            Wedding Budget Planner
          </p>
        </NavLink>

        {/* Navigation */}
        <nav className="flex-1 flex flex-col gap-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-sm transition-all duration-200 font-medium ${
                  isActive
                    ? 'bg-white text-[#69111F] shadow-sm'
                    : 'text-[#585858] hover:text-[#69111F] hover:bg-white/50'
                }`
              }
              data-testid={`nav-${item.path.replace('/', '')}`}
            >
              <item.icon className="w-5 h-5" strokeWidth={1.5} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User Section */}
        <div className="pt-6 border-t border-[#E6DCD3]">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                className="w-full justify-start gap-3 px-4 py-3 h-auto text-left hover:bg-white/50"
                data-testid="user-menu-trigger"
              >
                <div className="w-8 h-8 rounded-full bg-[#69111F] flex items-center justify-center">
                  <User className="w-4 h-4 text-[#F7F0E8]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#1A1A1A] truncate">{user?.name}</p>
                  <p className="text-xs text-[#585858] truncate">{user?.email}</p>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={handleLogout}
                className="text-red-600 cursor-pointer"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 min-h-screen p-8">
        {children}
      </main>
    </div>
  );
};

export default AdminLayout;
